/**
 * @fileoverview TintasManagementPage - Main page for managing ink parameters
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, CardTitle, Button, Input, FormGroup, Label, Alert, Spinner } from 'reactstrap';
import { RepositoryFactory } from '../repositories';
import { ParametroTintaModal } from '../components/ParametroTintaModal';
import { ParametrosTintaExpanded, Tinta, CreateParametrosTintaData, UpdateParametrosTintaData } from '../types/collections';
import { RepositoryError } from '../repositories/base/DataRepository';
import TableContainer from '../components/Common/DataTableContainer';
import Breadcrumbs from '../components/Common/Breadcrumb';

export const TintasManagementPage: React.FC = () => {
  // State management
  const [parametros, setParametros] = useState<ParametrosTintaExpanded[]>([]);
  const [parametrosInactivos, setParametrosInactivos] = useState<ParametrosTintaExpanded[]>([]);
  const [tintas, setTintas] = useState<Tinta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Paginación como en centro de costos
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingParametro, setEditingParametro] = useState<ParametrosTintaExpanded | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  
  // Delete/Restore confirmation states
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [parametroToDelete, setParametroToDelete] = useState<ParametrosTintaExpanded | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showRestoreAlert, setShowRestoreAlert] = useState(false);
  const [parametroToRestore, setParametroToRestore] = useState<ParametrosTintaExpanded | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Repositories
  const parametrosRepository = RepositoryFactory.getRepository('parametros_tinta') as any;
  const tintaRepository = RepositoryFactory.getRepository('tinta') as any;

  const loadData = useCallback(async () => {
    // Prevent duplicate calls
    if (isLoadingData) {
      return;
    }

    try {
      setIsLoadingData(true);
      setLoading(true);
      setError(null);

      // Load tintas for the modal
      const tintasData = await tintaRepository.getAllActive();
      setTintas(tintasData);

      // Load all parametros like centro de costos
      let allParametros: ParametrosTintaExpanded[] = [];

      if (searchTerm.trim()) {
        allParametros = await parametrosRepository.searchByTerm(searchTerm);
      } else {
        // Get all parametros (active and inactive) with expanded relations
        allParametros = await parametrosRepository.getAllWithExpand();
      }

      // Separate active and inactive like centro de costos
      const activos = allParametros.filter(parametro => parametro.estado !== false);
      const inactivos = allParametros.filter(parametro => parametro.estado === false);

      // Apply pagination only to active records
      const startIndex = pageIndex * pageSize;
      const endIndex = startIndex + pageSize;
      const activosPaginados = activos.slice(startIndex, endIndex);

      setParametros(activosPaginados);
      setParametrosInactivos(inactivos);
      setTotalRecords(activos.length);

    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar los datos. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
      setIsLoadingData(false);
    }
  }, [searchTerm, pageIndex, pageSize, parametrosRepository, tintaRepository]);

  // Load data when component mounts or filters change
  useEffect(() => {
    loadData();
  }, [searchTerm, pageIndex, pageSize]);

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    setPageIndex(0); // Reset to first page when searching
  }, []);

  // Delete/Restore handlers like centro de costos
  const handleRemove = (parametro: ParametrosTintaExpanded) => {
    setParametroToDelete(parametro);
    setShowDeleteAlert(true);
    // Scroll to top to show alert
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const confirmRemove = async () => {
    if (!parametroToDelete) return;
    setDeleting(true);
    try {
      await parametrosRepository.deactivate(parametroToDelete.id);
      await loadData();
      setShowDeleteAlert(false);
      setParametroToDelete(null);
      setError(null);
    } catch (err) {
      setError('Error al desactivar parámetro de tinta');
    }
    setDeleting(false);
  };

  const cancelRemove = () => {
    setShowDeleteAlert(false);
    setParametroToDelete(null);
  };

  const handleRestore = (parametro: ParametrosTintaExpanded) => {
    setParametroToRestore(parametro);
    setShowRestoreAlert(true);
    // Scroll to top to show alert
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const confirmRestore = async () => {
    if (!parametroToRestore) return;
    setRestoring(true);
    try {
      await parametrosRepository.activate(parametroToRestore.id);
      await loadData();
      setShowRestoreAlert(false);
      setParametroToRestore(null);
      setError(null);
    } catch (err) {
      setError('Error al restaurar parámetro de tinta');
    }
    setRestoring(false);
  };

  const cancelRestore = () => {
    setShowRestoreAlert(false);
    setParametroToRestore(null);
  };

  const handleAdd = useCallback(() => {
    setEditingParametro(null);
    setIsEdit(false);
    setModalError(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((parametro: ParametrosTintaExpanded) => {
    setEditingParametro(parametro);
    setIsEdit(true);
    setModalError(null);
    setIsModalOpen(true);
  }, []);


  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setEditingParametro(null);
    setIsEdit(false);
    setModalError(null);
  }, []);

  // Function to reload data
  const reloadParametros = () => {
    loadData();
  };

  const handleModalSave = useCallback(async (data: CreateParametrosTintaData | UpdateParametrosTintaData) => {
    try {
      setModalLoading(true);
      setModalError(null);

      if (isEdit && editingParametro) {
        await parametrosRepository.updateWithValidation(editingParametro.id, data as UpdateParametrosTintaData);
      } else {
        await parametrosRepository.createWithValidation(data as CreateParametrosTintaData);
      }

      // Close modal and reload data
      handleModalClose();
      await loadData();
    } catch (err: any) {
      console.error('Error saving parameter:', err);
      if (err instanceof RepositoryError) {
        setModalError(err.message);
      } else {
        setModalError('Error al guardar el parámetro. Por favor, inténtalo de nuevo.');
      }
    } finally {
      setModalLoading(false);
    }
  }, [isEdit, editingParametro, parametrosRepository, handleModalClose, loadData]);

  // Definir columnas como en centro de costos
  const columns = useMemo(() => [
    {
      header: "Nombre",
      accessorKey: "nombre",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Tintas",
      accessorKey: "tinta",
      cell: (info: any) => {
        const parametro = info.row.original;
        if (!parametro.tinta || parametro.tinta.length === 0) {
          return "Sin tintas asignadas";
        }
        
        if (parametro.expand?.tinta && Array.isArray(parametro.expand.tinta)) {
          const order = ['C', 'M', 'Y', 'K'];
          return parametro.expand.tinta
            .sort((a: any, b: any) => {
              const aIndex = order.indexOf(a.nombre);
              const bIndex = order.indexOf(b.nombre);
              
              if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
              }
              if (aIndex !== -1) return -1;
              if (bIndex !== -1) return 1;
              return a.nombre.localeCompare(b.nombre);
            })
            .map((tinta: any) => tinta.nombre)
            .join(", ");
        }
        
        return "Tintas asignadas";
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Pantone",
      accessorKey: "pantone",
      cell: (info: any) => {
        const parametro = info.row.original;
        return parametro.pantone || "-";
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Estado",
      accessorKey: "estado",
      cell: (info: any) =>
        info.getValue() === true
          ? <span className="badge bg-success">Activo</span>
          : <span className="badge bg-secondary">Inactivo</span>,
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Acciones",
      accessorKey: "acciones",
      cell: (info: any) => {
        const parametro = info.row.original;
        return (
          <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
            <Button color="warning" size="sm" onClick={() => handleEdit(parametro)} disabled={parametro.estado === false}>
              <i className="fas fa-pen me-1"></i> Editar
            </Button>
            <Button color="danger" size="sm" onClick={() => handleRemove(parametro)} disabled={parametro.estado === false}>
              <i className="fas fa-eraser me-1"></i> Eliminar
            </Button>
          </div>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
      meta: { style: { width: "1%", whiteSpace: "nowrap" } },
    },
  ], [parametros, tintas]);

  // Columnas para parámetros inactivos
  const columnsInactivos = useMemo(() => [
    {
      header: "Nombre",
      accessorKey: "nombre",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Tintas",
      accessorKey: "tinta",
      cell: (info: any) => {
        const parametro = info.row.original;
        if (!parametro.tinta || parametro.tinta.length === 0) {
          return "Sin tintas asignadas";
        }
        
        if (parametro.expand?.tinta && Array.isArray(parametro.expand.tinta)) {
          const order = ['C', 'M', 'Y', 'K'];
          return parametro.expand.tinta
            .sort((a: any, b: any) => {
              const aIndex = order.indexOf(a.nombre);
              const bIndex = order.indexOf(b.nombre);
              
              if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
              }
              if (aIndex !== -1) return -1;
              if (bIndex !== -1) return 1;
              return a.nombre.localeCompare(b.nombre);
            })
            .map((tinta: any) => tinta.nombre)
            .join(", ");
        }
        
        return "Tintas asignadas";
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Pantone",
      accessorKey: "pantone",
      cell: (info: any) => {
        const parametro = info.row.original;
        return parametro.pantone || "-";
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Estado",
      accessorKey: "estado",
      cell: (info: any) =>
        <span className="badge bg-danger">Inactivo</span>,
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Acciones",
      accessorKey: "acciones",
      cell: (info: any) => {
        const parametro = info.row.original;
        return (
          <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
            <Button color="success" size="sm" onClick={() => handleRestore(parametro)}>
              <i className="fas fa-undo me-1"></i> Restaurar
            </Button>
          </div>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
      meta: { style: { width: "1%", whiteSpace: "nowrap" } },
    },
  ], [parametrosInactivos]);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs maintitle="Dashboard" title="Parámetros y Materiales" breadcrumbItem="Tintas" />

        {/* Delete Alert */}
        {showDeleteAlert && parametroToDelete && (
          <Alert
            color="warning"
            isOpen={showDeleteAlert}
            toggle={cancelRemove}
            fade={true}
            transition={{ timeout: 150 }}
            className="d-flex align-items-center justify-content-between"
          >
            <div>
              <strong>¿Está seguro?</strong> Esto desactivará el parámetro de tinta <b>{parametroToDelete.nombre}</b>.
            </div>
            <div>
              <Button color="danger" size="sm" className="me-2" onClick={confirmRemove} disabled={deleting}>
                {deleting ? <Spinner size="sm" /> : <i className="fas fa-eraser me-1"></i>} Confirmar
              </Button>
              <Button color="secondary" size="sm" onClick={cancelRemove} disabled={deleting}>
                Cancelar
              </Button>
            </div>
          </Alert>
        )}

        {/* Restore Alert */}
        {showRestoreAlert && parametroToRestore && (
          <Alert
            color="info"
            isOpen={showRestoreAlert}
            toggle={cancelRestore}
            fade={true}
            transition={{ timeout: 150 }}
            className="d-flex align-items-center justify-content-between"
          >
            <div>
              <strong>¿Está seguro?</strong> Esto restaurará el parámetro de tinta <b>{parametroToRestore.nombre}</b>.
            </div>
            <div>
              <Button color="success" size="sm" className="me-2" onClick={confirmRestore} disabled={restoring}>
                {restoring ? <Spinner size="sm" /> : <i className="fas fa-undo me-1"></i>} Confirmar
              </Button>
              <Button color="secondary" size="sm" onClick={cancelRestore} disabled={restoring}>
                Cancelar
              </Button>
            </div>
          </Alert>
        )}

        {/* Main Content */}
        <Row>
          <Col>
            <Card>
              <CardBody>
                <CardTitle className="h4 d-flex justify-content-between align-items-center">
                  Parámetros de Tinta
                  <div>
                    <Button color="primary" size="sm" onClick={handleAdd}>
                      <i className="fas fa-plus me-1"></i> Agregar
                    </Button>
                    <Button
                      color="secondary"
                      size="sm"
                      className="ms-2"
                      title="Recargar tabla"
                      onClick={reloadParametros}
                    >
                      <i className="fas fa-sync-alt"></i>
                    </Button>
                  </div>
                </CardTitle>
                {/* Error Alert */}
                {error && (
                  <Alert color="danger" className="mb-3" data-testid="error-message" role="alert">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                  </Alert>
                )}

                {/* Search and Filters */}
                <Row className="mb-3">
                  <Col md={8}>
                    <FormGroup>
                      <Label for="search-input">Buscar</Label>
                      <Input
                        type="text"
                        id="search-input"
                        placeholder="Buscar parámetros de tinta..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="form-control"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Data Table */}
                <div className="table-rep-plugin">
                  <div className="mb-0" data-pattern="priority-columns">
                    <TableContainer
                      columns={columns}
                      data={parametros}
                      isGlobalFilter={false}
                      isPagination={true}
                      isCustomPageSize={true}
                      pageIndex={pageIndex}
                      setPageIndex={setPageIndex}
                      pageSize={pageSize}
                      setPageSize={setPageSize}
                      totalRecords={totalRecords}
                      pagination="pagination"
                      paginationWrapper='dataTables_paginate paging_simple_numbers'
                      tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                      theadClass=""
                      divClassName=""
                      isBordered={false}
                      SearchPlaceholder=""
                      buttonClass=""
                      buttonName=""
                      isAddButton={false}
                      handleUserClick={() => {}}
                      isJobListGlobalFilter={false}
                    />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Tabla de Parámetros Inactivos */}
        <Row className="mt-4">
          <Col>
            <Card>
              <CardBody>
                <CardTitle className="h4 d-flex justify-content-between align-items-center">
                  Parámetros de Tinta Inactivos
                  <div>
                    <Button
                      color="secondary"
                      size="sm"
                      title="Recargar tabla"
                      onClick={loadData}
                    >
                      <i className="fas fa-sync-alt"></i>
                    </Button>
                  </div>
                </CardTitle>
                <div className="table-rep-plugin">
                  <div className="mb-0" data-pattern="priority-columns">
                    <TableContainer
                      columns={columnsInactivos}
                      data={parametrosInactivos}
                      isGlobalFilter={false}
                      isPagination={false}
                      isCustomPageSize={false}
                      tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                      theadClass=""
                      divClassName=""
                      isBordered={false}
                      SearchPlaceholder=""
                      buttonClass=""
                      buttonName=""
                      isAddButton={false}
                      handleUserClick={() => {}}
                      isJobListGlobalFilter={false}
                      paginationWrapper=""
                      pagination=""
                      pageIndex={0}
                      setPageIndex={() => {}}
                      pageSize={10}
                      setPageSize={() => {}}
                    />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Parameter Modal */}
        <ParametroTintaModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={handleModalSave}
          tintas={tintas}
          parametro={editingParametro}
          isEdit={isEdit}
          loading={modalLoading}
          error={modalError}
        />
      </Container>
    </div>
  );
};
